import { Mql, MqlCompiledQuery } from '@deqo/mql-js';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { LabelValue } from '../../../../platform/types/LabelValue';
import { Colors } from '../../../../ui';
import { TextButton } from '../../../../ui/TextButton';
import { useResolve } from '../../../Container';
import { createButton } from '../../../canvas/commands/ComponentCommands';
import { MqlEditor, } from '../../../common/MqlEditor';
import { Flexer, Title } from '../../../dashboard/elements/LayoutElements';
import { EmptyMqlCompiledQuery } from '../EmptyMqlCompiledQuery';
import { CreateLensInfo, LensInfo } from '../LensInfo';
import { useMqlCompleter } from '../MqlUtils';

const Label = styled.label`
    flex: 1 1 auto;
    color: ${Colors.text};
`;

const InputFrame = styled.div`
    display: flex;
    flex-direction: row;
    padding: .2rem 0;

    * > {
        flex: 1 1 auto;
    }

    ${Label} > {
        flex: 0 0 3rem;
    }
`;

const TextArea = styled.textarea`
    padding: .125rem .375rem;
    width: 100%;
    border: solid 1px ${Colors.border};
    border-radius: .25rem;
    background: white;
`;

const MqlEditorFrame = styled.div`
    position: relative;
    width: 34rem; 
    height: 16rem;
    border: solid 1px ${Colors.border};
    border-radius: .25rem;
    background: white;
    overflow: hidden;
`;

const Select = styled.select`
    padding: .125rem;
    width: 100%;
    border: solid 1px ${Colors.border};
    border-radius: .25rem;
    background: white;
`;

const InputAcceptMount = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: .25rem;
    bottom: 0;    
`;

const Stack = styled.div`
    display: grid;
    grid-auto-rows: 1fr;
    row-gap: .5rem;
    border-top: solid 1px ${Colors.border};
    padding: .75rem 1rem;
`;

const StackSplit = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
`;

const NativeSelect = React.memo<{ options: LabelValue[], value: string, onSelect: (newValue: string) => void }>(({ options, value, onSelect }) =>
    <Select value={value} onChange={e => onSelect(e.target.value as any)}>
        {options.map(x =>
            <option key={x.value} value={x.value}>
                {x.label}
            </option>
        )}
    </Select>
);

type OptionInputProps = {
    label: string,
    options: LabelValue[],
    value: any,
    onApply: (newValue: any) => void,
};

const OptionInput = React.memo<OptionInputProps>(({ label, options, value, onApply }) => {

    return <InputFrame>
        <Label>{label}</Label>
        <Flexer relative width="34">
            <NativeSelect options={options} value={value} onSelect={onApply} />
        </Flexer>
    </InputFrame>;
});

// const useSuggestionScope = () => {
//     const reflector = useResolve(LensReflector);
//     const objects = useResolve(ObjectManager);
//     const mql = useResolve(EditorMqlEngine);

//     return {
//         suggestIdentifiers: (fragment: string) => {
//             if (fragment === '') {
//                 return [MqlSuggestionItem('type', 'property')];
//             }

//             const hits = mql.exec(fragment, Array.from(objects.getAll()));
//             const aspects = reflector.reflect(hits);

//             // const types = new Set(
//             //     hits.map(x => Component.describe(x))
//             // );

//             // const props = new Set(
//             //     Array.from(types).flatMap(t => t.properties.map(p => p.name))
//             // );

//             // const tags = new Set(
//             //     hits.flatMap(x => x.polarize(Tag)).map(x => x.name.toLowerCase())
//             // );

//             // const all = [
//             //     ...Array.from(props).map(x => MqlSuggestionItem(x, 'property')),
//             //     ...Array.from(tags).map(x => MqlSuggestionItem(x, 'assocation')),
//             // ];

//             return aspects
//                 .sort((a, b) => a.name.localeCompare(b.name))
//                 .map(x => MqlSuggestionItem(x.name, x.kind));
//         },
//         suggestValues: (identifier: string) => {
//             if (identifier === 'type') {
//                 return Manifest
//                     .getTypesByConvention(Physical)
//                     .map(x => `"${Component.describe(x).name}"`).sort()
//                     .map(x => MqlSuggestionItem(x, 'value'));
//             }
//             return [];
//         },
//     };
// };

type QueryInputProps = {
    label: string,
    value: MqlCompiledQuery,
    onApply: (value: MqlCompiledQuery) => void,
};

const QueryInput = React.memo<QueryInputProps>(({ label, value, onApply }) => {

    const completer = useMqlCompleter();
    const [code, setCode] = useState(value?.source || '');

    const onChange = useCallback((nextValue: string) => {
        setCode(nextValue);
        try {
            const next = Mql.compile(nextValue);
            console.log('Success:', next.source);
            onApply(next);
        }
        catch (e) {
            console.error(e);
            onApply(null);
        }
    }, [onApply]);

    return <InputFrame>
        <Label>{label}</Label>
        <MqlEditorFrame>
            <MqlEditor completer={completer} initialValue={code} onChange={onChange} />
        </MqlEditorFrame>
    </InputFrame>;
});

export type CreateOrEditLensViewProps = {
    lens?: LensInfo,
    onCreate: (lens: LensInfo) => void,
};

export const CreateOrEditLensView = React.memo<CreateOrEditLensViewProps>(({ lens, onCreate }) => {

    const dispatch = useResolve(CommandDispatcher);

    const [typeOpts] = useState(() => [
        LabelValue('Fade', 'fade'),
        LabelValue('Highlight', 'highlight'),
    ]);

    const [type, setType] = useState(typeOpts.find(x => x.value === lens?.type) ? lens?.type : 'fade');
    const [filter, setFilter] = useState(lens?.filter || EmptyMqlCompiledQuery);

    const onApplyLens = useCallback(() => {
        onCreate(CreateLensInfo(type, filter));
    }, [type, filter]);

    const onSaveLens = useCallback(() => {
        const script = `lensApply ${type} ${JSON.stringify(filter.source)}`;
        dispatch(createButton, [script]);
    }, [type, filter]);

    return <Flexer width="42">
        <Flexer padding=".5 1" background={Colors.gray50}>
            <Title flush size="2xs" color={Colors.text} uppercase weight="500">Create Lens</Title>
        </Flexer>
        <Flexer flex="column" padding="1">
            <OptionInput label="Apply:" options={typeOpts} value={type} onApply={setType} />
            <QueryInput label="Where:" value={filter} onApply={setFilter} />
            <Flexer grow flex="row" justify="end" gap="1">
                <TextButton style={{ fontSize: '.875rem' }} onClick={onApplyLens}>Apply Lens</TextButton>
                <TextButton style={{ fontSize: '.875rem' }} onClick={onSaveLens}>Save As Button</TextButton>
            </Flexer>
        </Flexer>
    </Flexer>;

});
